import ExcelJS from 'exceljs';
import { formatSingaporeDate } from '../utils/dateTime';
import type {
  CitAttachmentRef,
  CitDocumentRow,
  CitItemRow,
  CitOffsetLine,
  CitOffsetRow,
  CustomiseItemsTransferFormData,
} from '../types/applicationForm';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import {
  collectCitSupportingAttachments,
  downloadCitAttachment,
} from './citSubmissions';

const TEMPLATE_URL = '/assets/cit/stock-transfer-form.xlsx';
const BUCKET = 'cit-attachments';
const ITEM_START_ROW = 9;
const ITEM_MAX_ROW = 14;

export type CitStockTransferFormInput = {
  form: CustomiseItemsTransferFormData;
  offset: CitOffsetRow;
  transferReason: string;
  salespersonCode?: string;
};

type InvoiceBlock = {
  title: string;
  detail: string;
  reason?: string;
  /** Deposit invoice amount for TOTAL AMOUNT column (not offsets). */
  amount?: string;
};

function formatAmount(value: string): string {
  const n = num(value);
  if (!Number.isFinite(n)) return value.trim();
  return n.toLocaleString('en-SG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function num(value: string): number {
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : NaN;
}

function filledDeposits(form: CustomiseItemsTransferFormData): CitDocumentRow[] {
  return form.deposit_invoices.filter(
    (row) =>
      row.number.trim() ||
      row.invoice_amount.trim() ||
      row.file ||
      row.lines.some((line) => line.item_no.trim() || line.quantity.trim()) ||
      row.payment_receipts.some((receipt) => receipt.number.trim() || receipt.file),
  );
}

function depositTotalQty(row: CitDocumentRow): number {
  return row.lines.reduce((sum, line) => {
    const q = num(line.quantity);
    return sum + (Number.isFinite(q) ? q : 0);
  }, 0);
}

function offsetLineFilled(line: CitOffsetLine): boolean {
  return Boolean(
    line.item_no.trim() ||
      line.quantity.trim() ||
      line.warehouse_defu_qty.trim() ||
      line.warehouse_depot_qty.trim(),
  );
}

function filledOffsetLines(offset: CitOffsetRow): CitOffsetLine[] {
  return offset.lines.filter(offsetLineFilled);
}

function filledOffsets(form: CustomiseItemsTransferFormData): CitOffsetRow[] {
  return form.offset_invoices.filter(
    (row) =>
      row.number.trim() ||
      row.invoice_amount.trim() ||
      row.file ||
      row.stock_transfer_no.trim() ||
      row.lines.some(offsetLineFilled),
  );
}

function findItem(form: CustomiseItemsTransferFormData, itemNo: string): CitItemRow | undefined {
  const code = itemNo.trim();
  return form.items.find((row) => row.item_no.trim() === code);
}

/** How many text lines fit in one Invoice No. cell before opening the next. */
const INVOICE_CELL_MAX_LINES = 3;

/**
 * Pack offset invoice numbers into cells: continue on the next line within the
 * same cell; only open a new cell when the current one is full.
 * First cell: "Offset Invoice" title + as many numbers as fit under it.
 */
function pushPackedOffsetInvoiceBlocks(
  blocks: InvoiceBlock[],
  offsets: CitOffsetRow[],
): void {
  const numbers = offsets.map((row) => row.number.trim()).filter(Boolean);
  if (numbers.length === 0) return;

  let index = 0;
  const firstCapacity = Math.max(1, INVOICE_CELL_MAX_LINES - 1); // title uses 1 line
  const firstChunk = numbers.slice(index, index + firstCapacity);
  blocks.push({
    title: 'Offset Invoice',
    detail: firstChunk.join('\n'),
  });
  index += firstChunk.length;

  while (index < numbers.length) {
    const chunk = numbers.slice(index, index + INVOICE_CELL_MAX_LINES);
    blocks.push({
      title: '',
      detail: chunk.join('\n'),
    });
    index += chunk.length;
  }
}

/** Build invoice-no. blocks: 100% or 50%/Bal 50% deposits + packed offset invoices. */
export function buildCitInvoiceBlocks(
  form: CustomiseItemsTransferFormData,
  offset: CitOffsetRow,
  transferReason: string,
): InvoiceBlock[] {
  const deposits = filledDeposits(form);
  const offsets = filledOffsets(form);
  const blocks: InvoiceBlock[] = [];

  const scQtySum = filledOffsetLines(offset).reduce((sum, line) => {
    const sc = num(findItem(form, line.item_no)?.sales_confirmation_qty ?? '');
    return sum + (Number.isFinite(sc) ? sc : 0);
  }, 0);

  const isHundredPercent =
    deposits.length === 1 &&
    Number.isFinite(scQtySum) &&
    scQtySum > 0 &&
    Math.abs(depositTotalQty(deposits[0]) - scQtySum) < 1e-9;

  if (isHundredPercent) {
    blocks.push({
      title: '100% Deposit Invoice',
      detail: deposits[0].number.trim(),
      reason: transferReason,
      amount: formatAmount(deposits[0].invoice_amount),
    });
  } else {
    deposits.forEach((deposit, index) => {
      const title =
        index === 0 ? '50% Deposit Invoice' : index === 1 ? 'Bal 50% Deposit Invoice' : 'Deposit Invoice';
      blocks.push({
        title,
        detail: deposit.number.trim(),
        reason: index < 2 ? transferReason : undefined,
        amount: formatAmount(deposit.invoice_amount),
      });
    });
  }

  pushPackedOffsetInvoiceBlocks(blocks, offsets);

  return blocks;
}

function sgDateLabel(date = new Date()): string {
  return formatSingaporeDate(date.toISOString());
}

function blobToBase64(blob: Blob): Promise<string> {
  return blob.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  });
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function setInvoiceRichText(cell: ExcelJS.Cell, title: string, detail: string) {
  const parts: ExcelJS.RichText[] = [];
  if (title.trim()) {
    parts.push({
      font: { bold: true, underline: true, size: 10, name: 'Calibri' },
      text: `${title.trim()}${detail.trim() ? '\n' : ''}`,
    });
  }
  if (detail.trim()) {
    parts.push({
      font: { size: 10, name: 'Calibri' },
      text: detail,
    });
  }
  cell.value = { richText: parts };
  cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
}

function middleAlign(cell: ExcelJS.Cell, horizontal: ExcelJS.Alignment['horizontal'] = 'center') {
  cell.alignment = {
    ...(cell.alignment ?? {}),
    wrapText: true,
    vertical: 'middle',
    horizontal,
  };
}

export async function buildCitStockTransferWorkbook(
  input: CitStockTransferFormInput,
): Promise<Blob> {
  const { form, offset, transferReason, salespersonCode } = input;
  const lines = filledOffsetLines(offset);
  if (lines.length === 0) {
    throw new Error('Offset invoice needs at least one item.');
  }
  if (lines.length > ITEM_MAX_ROW - ITEM_START_ROW + 1) {
    throw new Error('At most 6 items can fit on the stock transfer form.');
  }
  for (const [index, line] of lines.entries()) {
    const item = findItem(form, line.item_no);
    if (!item?.item_no.trim()) {
      throw new Error(`Offset item ${index + 1}: item no. must match an item in General.`);
    }
    if (!item.item_no_transfer.trim()) {
      throw new Error(`Enter Item no. (Transfer) in General for ${item.item_no.trim()}.`);
    }
  }
  if (!form.customer_code.trim() || !form.customer_name.trim()) {
    throw new Error('Customer code and name are required.');
  }
  if (!offset.stock_transfer_no.trim()) {
    throw new Error('Save the entry first so Stock transfer no. can be generated.');
  }

  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error('Stock transfer form template is missing.');
  const templateBuf = await res.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(templateBuf);
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new Error('Stock transfer form template has no sheet.');

  const k4 = sheet.getCell('K4');
  k4.value = offset.stock_transfer_no.trim();
  middleAlign(k4, 'left');
  const k5 = sheet.getCell('K5');
  k5.value = sgDateLabel();
  middleAlign(k5, 'left');
  const k6 = sheet.getCell('K6');
  k6.value = salespersonCode?.trim() || '';
  middleAlign(k6, 'left');

  // Upper-left blank: sales confirmation no. only (no label).
  const a4 = sheet.getCell('A4');
  a4.value = form.sales_confirmation_no.trim();
  middleAlign(a4, 'left');

  lines.forEach((line, index) => {
    const row = ITEM_START_ROW + index;
    const item = findItem(form, line.item_no)!;

    const a = sheet.getCell(`A${row}`);
    a.value = item.item_no.trim();
    middleAlign(a);
    const b = sheet.getCell(`B${row}`);
    b.value = item.item_no_transfer.trim();
    middleAlign(b);
    const c = sheet.getCell(`C${row}`);
    c.value = item.description.trim();
    middleAlign(c);

    const d = sheet.getCell(`D${row}`);
    d.value = 'Defu:\nDepot:';
    middleAlign(d, 'left');
    const e = sheet.getCell(`E${row}`);
    e.value = `${line.warehouse_defu_qty.trim() || '0'}\n${line.warehouse_depot_qty.trim() || '0'}`;
    middleAlign(e, 'right');

    const f = sheet.getCell(`F${row}`);
    f.value = item.uom.trim();
    middleAlign(f);

    if (index === 0) {
      const g = sheet.getCell(`G${row}`);
      g.value = `${form.customer_code.trim()} — ${form.customer_name.trim()}`;
      middleAlign(g);
    }
  });

  const blocks = buildCitInvoiceBlocks(form, offset, transferReason.trim());
  for (let i = 0; i < blocks.length && ITEM_START_ROW + i <= ITEM_MAX_ROW; i += 1) {
    const row = ITEM_START_ROW + i;
    const block = blocks[i]!;
    setInvoiceRichText(sheet.getCell(`H${row}`), block.title, block.detail);
    if (block.amount?.trim()) {
      const amountCell = sheet.getCell(`I${row}`);
      amountCell.value = block.amount.trim();
      middleAlign(amountCell);
    }
    if (block.reason?.trim()) {
      const reasonCell = sheet.getCell(`J${row}`);
      reasonCell.value = block.reason.trim();
      middleAlign(reasonCell);
    }
  }

  const out = await workbook.xlsx.writeBuffer();
  return new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

async function convertXlsxBase64ToPdf(xlsxBase64: string, fileStem: string): Promise<string> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) throw new Error('Supabase not configured');

  const endpoint = `${url.replace(/\/$/, '')}/functions/v1/application-form-sync`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: 'convert_xlsx_pdf',
      sheet_xlsx_base64: xlsxBase64,
      file_stem: fileStem,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || body.ok === false) {
    throw new Error(
      typeof body.error === 'string'
        ? body.error
        : 'PDF convert failed. Check Account Creation SharePoint is configured for Graph convert.',
    );
  }
  const pdfB64 = typeof body.pdf_base64 === 'string' ? body.pdf_base64 : '';
  if (!pdfB64) throw new Error('PDF convert returned empty result.');
  return pdfB64;
}

async function appendBlobAsPdfPages(
  target: import('pdf-lib').PDFDocument,
  blob: Blob,
  fileName: string,
): Promise<boolean> {
  const { PDFDocument } = await import('pdf-lib');
  const type = (blob.type || '').toLowerCase();
  const lowerName = fileName.toLowerCase();
  const bytes = new Uint8Array(await blob.arrayBuffer());

  if (type === 'application/pdf' || lowerName.endsWith('.pdf')) {
    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
    const pages = await target.copyPages(src, src.getPageIndices());
    for (const page of pages) target.addPage(page);
    return true;
  }

  if (type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(lowerName)) {
    let image;
    if (type === 'image/png' || lowerName.endsWith('.png')) {
      image = await target.embedPng(bytes);
    } else if (type === 'image/jpeg' || /\.jpe?g$/i.test(lowerName)) {
      image = await target.embedJpg(bytes);
    } else {
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;
      ctx.drawImage(bmp, 0, 0);
      const pngBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/png');
      });
      if (!pngBlob) return false;
      image = await target.embedPng(new Uint8Array(await pngBlob.arrayBuffer()));
    }
    const dims = image.scale(1);
    const page = target.addPage([dims.width, dims.height]);
    page.drawImage(image, { x: 0, y: 0, width: dims.width, height: dims.height });
    return true;
  }

  // Office / other formats are skipped (cannot embed with pdf-lib).
  return false;
}

async function mergeStockTransferWithSupporting(
  stPdfBytes: Uint8Array,
  form: CustomiseItemsTransferFormData,
): Promise<Uint8Array> {
  const { PDFDocument } = await import('pdf-lib');
  const merged = await PDFDocument.create();
  const stDoc = await PDFDocument.load(stPdfBytes, { ignoreEncryption: true });
  const stPages = await merged.copyPages(stDoc, stDoc.getPageIndices());
  for (const page of stPages) merged.addPage(page);

  const supporting = collectCitSupportingAttachments(form);
  for (const ref of supporting) {
    try {
      const blob = await downloadCitAttachment(ref.path);
      await appendBlobAsPdfPages(merged, blob, ref.fileName || ref.path);
    } catch {
      // Skip missing/unreadable attachments; ST form itself still saves.
    }
  }

  return merged.save();
}

/**
 * Fill official template → temp Graph PDF convert → append supporting docs →
 * store only under cit-attachments. Supporting order:
 * deposit1, payment receipts for deposit1, deposit2…, all offset invoice files, sales confirmation.
 * Prior stock-transfer PDFs are not appended — only this newly created form is first.
 */
export async function createCitStockTransferFormPdf(options: {
  submissionId: string;
  input: CitStockTransferFormInput;
  /** Overlay circular red creator/submitter seal on the ST form page(s). */
  creatorName?: string;
}): Promise<CitAttachmentRef> {
  const xlsxBlob = await buildCitStockTransferWorkbook(options.input);
  const xlsxBase64 = await blobToBase64(xlsxBlob);
  const stNo = options.input.offset.stock_transfer_no.trim().replace(/[\\/:*?"<>|]+/g, '_');
  let pdfB64 = await convertXlsxBase64ToPdf(xlsxBase64, stNo || 'stock-transfer');

  const creatorName = options.creatorName?.trim();
  if (creatorName) {
    const { CIT_CREATOR_STAMP_SIZE_PT, stampCreatorNameOnPdfBase64 } = await import(
      './creatorNameStamp'
    );
    pdfB64 = await stampCreatorNameOnPdfBase64(pdfB64, creatorName, {
      placement: 'cit-requested-by',
      sizePt: CIT_CREATOR_STAMP_SIZE_PT,
    });
  }

  const stBytes = base64ToUint8(pdfB64);
  const bytes = await mergeStockTransferWithSupporting(stBytes, options.input.form);
  const fileName = `${stNo || 'stock-transfer'}.pdf`;
  const path = `${options.submissionId}/stock_transfer/${options.input.offset.id}/${Date.now()}_${fileName}`;

  const db = getDbSupabase('forms');
  const { error } = await db.storage.from(BUCKET).upload(path, bytes, {
    upsert: false,
    contentType: 'application/pdf',
  });
  if (error) throw new Error(error.message || formatSupabaseError(error));

  return { path, fileName };
}
